import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { MAX_AI_INSTRUCTIONS, type FormDefinition } from '../../types'
import { setAIReview } from './model'

interface Props {
    def: FormDefinition
    edit: (fn: (d: FormDefinition) => FormDefinition) => void
}

const EXAMPLES = [
    'Treat any gift worth more than 100 EUR as high risk.',
    'If a government official is involved, always raise a concern and ask whether pre-approval was obtained.',
    'Flag when the organisation is also a Unisys client, supplier or competitor.',
]

/** Per-form settings for the AI risk summary reviewers see. */
export default function AiReviewTab({ def, edit }: Props) {
    const enabled = !def.ai?.disabled
    const text = def.ai?.instructions ?? ''

    return (
        <Card className="max-w-3xl">
            <CardHeader>
                <CardTitle>AI risk summary</CardTitle>
                <CardDescription>
                    Reviewers see a short, AI-written summary of each submission: what is being asked, the main concerns,
                    and what is missing. It is a starting point for the reviewer, never a decision.
                </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-6">
                <Field orientation="horizontal">
                    <Switch
                        id="ai-enabled"
                        checked={enabled}
                        onCheckedChange={(on) => edit((d) => setAIReview(d, { disabled: !on }))}
                    />
                    <FieldLabel htmlFor="ai-enabled">Give reviewers an AI risk summary for this form</FieldLabel>
                </Field>

                <Field>
                    <FieldLabel htmlFor="ai-instructions">Review rules for this form</FieldLabel>
                    <FieldDescription>
                        Write in plain English what the AI should look out for. These are added to what it already knows:
                        the answers and the rules you set up on the Rules tab.
                    </FieldDescription>
                    <Textarea
                        id="ai-instructions"
                        rows={7}
                        maxLength={MAX_AI_INSTRUCTIONS}
                        disabled={!enabled}
                        value={text}
                        placeholder="For example: Treat any gift worth more than 100 EUR as high risk."
                        onChange={(e) => edit((d) => setAIReview(d, { instructions: e.target.value }))}
                    />
                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                        <span>{text.length} / {MAX_AI_INSTRUCTIONS}</span>
                    </div>
                </Field>

                <div className="flex flex-col gap-2">
                    <p className="text-sm font-medium">Examples</p>
                    <ul className="flex flex-col gap-1" role="list">
                        {EXAMPLES.map((ex) => (
                            <li key={ex} className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
                                <span>{ex}</span>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    disabled={!enabled || text.includes(ex)}
                                    onClick={() => edit((d) => setAIReview(d, { instructions: text.trim() ? `${text.trim()}\n${ex}` : ex }))}
                                >
                                    Add
                                </Button>
                            </li>
                        ))}
                    </ul>
                </div>

                <p className="text-sm text-muted-foreground">
                    Answers are sent to the company’s AI service to write the summary. Reviewers see it; the person who
                    submitted the request does not.
                </p>
            </CardContent>
        </Card>
    )
}
